// src/components/asistencia/MarcarAsistenciaMovil.js
// Marca de asistencia desde el CELULAR del propio empleado (no el kiosko).
// Como ya está logueado, NO pide PIN: el sistema ya sabe quién es.
// Flujo: pide ubicación (GPS) -> verifica que esté en la oficina (geocerca) ->
// cámara confirma la cara 1:1 contra su rostro registrado -> foto WebP -> registra.
// Filosofía: si está CLARAMENTE fuera del área, NO deja marcar (que use el kiosko).
// Si la cara no coincide o la ubicación queda en el límite, marca igual pero
// "para revisión" (no bloquea al empleado real con GPS impreciso).
// Todo corre en el navegador (Q0): GPS y reconocimiento facial son del teléfono.
//
// MODO JORNADA (23/10/2026, quien tiene horario "sin hora fija", los mensajeros): marca el INICIO y el FIN de su
// jornada a cualquier hora y desde donde esté. SIN geocerca: la ubicación se busca unos segundos y se guarda como
// evidencia, pero no impide marcar (sin ubicación → la marca va a Revisión). Cara, sonrisa y foto: idénticas.
// Se guardan como ENTRADA / SALIDA con `jornada: true`. Si su última jornada quedó sin fin, se le avisa.
// El modo lo decide el horario vigente de HOY en su expediente (1 lectura; el colaborador no puede editarlo: reglas).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db, storage } from '../../services/firebase';
import { collection, addDoc, getDoc, getDocs, doc, query, where, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { cargarModelos, detectarRostroConTope, fotoWebpDesde, evaluarMatch, detectarPruebaVida } from './faceModels';
import { horarioVigente, esSinHoraFija } from './asistenciaEngine';
import { ClockIcon, CheckCircleIcon, ExclamationTriangleIcon, XMarkIcon, MapPinIcon, FaceSmileIcon, FaceFrownIcon } from '../ui/Icons';

const TIPOS = {
  ENTRADA: 'Entrada',
  SALIDA_ALMUERZO: 'Salida a almuerzo',
  REGRESO_ALMUERZO: 'Regreso de almuerzo',
  SALIDA: 'Salida'
};
// Etiquetas de los BOTONES (más claras para el personal); TIPOS queda para los textos.
const ETIQUETA_BOTON = {
  ENTRADA: 'Marcar Entrada',
  SALIDA_ALMUERZO: 'Inicio Almuerzo',
  REGRESO_ALMUERZO: 'Fin Almuerzo',
  SALIDA: 'Marcar Salida'
};
// Modo jornada (sin hora fija): las mismas marcas, con sus palabras.
const TIPOS_JORNADA = { ENTRADA: 'Inicio de jornada', SALIDA: 'Fin de jornada' };
const ETIQUETA_BOTON_JORNADA = { ENTRADA: 'Iniciar jornada', SALIDA: 'Terminar jornada' };

// Próxima marca de una jornada: sin inicio → inicio; con inicio y sin fin → fin; con los dos → cerrada ([]).
function opcionesJornada(marcasHoy) {
  const inicio = marcasHoy.find((m) => m.tipo === 'ENTRADA');
  if (!inicio) return ['ENTRADA'];
  const fin = marcasHoy.some((m) => m.tipo === 'SALIDA' && minHora(m) >= minHora(inicio));
  return fin ? [] : ['SALIDA'];
}
// 'YYYY-MM-DD' de hace n días (zona GT).
function fechaGTHace(n) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guatemala', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(Date.now() - n * 86400000));
}

const emailKeyDe = (email) => (email || '').trim().toLowerCase();
// 🔴 8 caracteres al azar en el nombre de la foto (F4/F5, 29/10/2026): el porqué está en KioskoAsistencia.js.
const parteAlAzar = () => Array.from(window.crypto.getRandomValues(new Uint8Array(4)), (b) => b.toString(16).padStart(2, '0')).join('');

function fechaHoraGT() {
  const ahora = new Date();
  const fecha = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guatemala', year: 'numeric', month: '2-digit', day: '2-digit' }).format(ahora);
  const hora = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Guatemala', hour: '2-digit', minute: '2-digit', hour12: false }).format(ahora);
  return { fecha, hora };
}

function opcionesSiguiente(ultimo) {
  if (!ultimo) return ['ENTRADA'];
  if (ultimo === 'ENTRADA') return ['SALIDA_ALMUERZO', 'SALIDA'];
  if (ultimo === 'SALIDA_ALMUERZO') return ['REGRESO_ALMUERZO'];
  if (ultimo === 'REGRESO_ALMUERZO') return ['SALIDA'];
  return [];
}
const minHora = (m) => { if (!m || !m.hora) return 99999; const [h, mm] = m.hora.split(':').map(Number); return h * 60 + mm; };
const hora12 = (hhmm) => { if (!hhmm) return ''; const [h, m] = hhmm.split(':').map(Number); return `${(h % 12) || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'a. m.' : 'p. m.'}`; };

// Distancia en metros entre dos coordenadas (haversine)
function distanciaMetros(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// 🔴 Ubicación REFINADA, no la primera lectura (25/08/2026). La primera posición que
// entrega el teléfono suele ser la estimación rápida por WiFi/antenas, que puede caer
// a cientos de metros — con eso se rechazaba a gente parada DENTRO de la oficina
// (Google Maps se veía bien porque refina continuamente; acá se sentenciaba con la
// primera foto borrosa). Ahora se escucha `watchPosition` hasta `maxMs`, se guarda la
// MEJOR lectura vista (menor margen de error) y se acepta temprano apenas una baja de
// `objetivoM`. En la práctica resuelve en 3-8 segundos.
function obtenerUbicacion({ objetivoM = 25, maxMs = 20000, onLectura } = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('no-geo')); return; }
    let mejor = null;
    let watchId = null;
    let timer = null;
    let terminado = false;
    const terminar = (err) => {
      if (terminado) return;
      terminado = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (timer) clearTimeout(timer);
      if (mejor) resolve(mejor);
      else reject(err || new Error('timeout'));
    };
    timer = setTimeout(() => terminar(new Error('timeout')), maxMs);
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const lectura = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy || 9999 };
        if (!mejor || lectura.accuracy < mejor.accuracy) {
          mejor = lectura;
          if (onLectura) { try { onLectura(mejor.accuracy); } catch (e) { /* noop */ } }
        }
        if (mejor.accuracy <= objetivoM) terminar();
      },
      // Solo el permiso DENEGADO corta de una; los errores transitorios (señal débil,
      // timeout interno) se ignoran — el reloj de arriba es el que manda.
      (err) => { if (err && err.code === 1) terminar(err); },
      { enableHighAccuracy: true, timeout: maxMs, maximumAge: 0 }
    );
  });
}

function getDispositivoId() {
  try {
    let id = localStorage.getItem('asistencia_dispositivo_id');
    if (!id) { id = 'movil-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36); localStorage.setItem('asistencia_dispositivo_id', id); }
    return id;
  } catch (e) { return 'movil-?'; }
}

function MarcarAsistenciaMovil({ config, onClose }) {
  const { user } = useAuth();
  const miEmail = (user && user.email) || '';
  const miNombre = (user && (user.nombre || user.email)) || '';

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const procesandoRef = useRef(false);
  const intentosSinRostroRef = useRef(0); // intentos seguidos rechazados por "sin rostro" (al 2º se registra para revisión)
  const intentosCongeladaRef = useRef(0); // intentos seguidos con la cámara congelada (al 2º se registra para revisión)
  const montadoRef = useRef(true);        // guard para no tocar estado tras desmontar (preparar() es re-invocable por "Reintentar")

  const [fase, setFase] = useState('cargando'); // cargando | sinRostro | configError | bloqueadoUbicacion | fueraArea | cerrada | listo | procesando | rechazo | resultado
  const [mensaje, setMensaje] = useState('');
  const [empleado, setEmpleado] = useState(null);   // doc de asistencia_rostros (con tomas)
  const [opciones, setOpciones] = useState(['ENTRADA']);
  const [salidaHora, setSalidaHora] = useState('');
  const [geo, setGeo] = useState(null);             // { lat, lng, accuracy, distancia }
  const [resultado, setResultado] = useState(null); // { tipo, hora, match, avisarRevision }
  const [estadoPrueba, setEstadoPrueba] = useState(null); // null | 'SONRIA' | 'PRIMERO_SERIO' | 'VERIFICANDO' | 'GUARDANDO' | 'CHECK' (guía sobre el video)
  const [tipoProceso, setTipoProceso] = useState(null);   // tipo de marca en proceso (para el saludo)
  const [motivoRechazo, setMotivoRechazo] = useState(''); // 'rostro' | 'camara' | 'red' (pantalla completa "NO se registró")
  const [fotoCongelada, setFotoCongelada] = useState(null); // object URL de la foto registrada (se congela al terminar el reconocimiento)
  const fotoUrlRef = useRef(null);
  const [modoJornada, setModoJornada] = useState(false);   // horario "sin hora fija": inicio y fin de jornada, sin geocerca
  const [avisoSinFin, setAvisoSinFin] = useState('');      // su última jornada quedó sin fin (aviso al mensajero)
  const tipoTexto = (t) => (modoJornada ? TIPOS_JORNADA[t] : TIPOS[t]);
  const etiquetaBoton = (t) => (modoJornada ? ETIQUETA_BOTON_JORNADA[t] : ETIQUETA_BOTON[t]);

  const radio = Number((config && config.radioMetros) || 35);
  const oficinaLat = config && config.oficinaLat;
  const oficinaLng = config && config.oficinaLng;

  const detenerCamara = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  const iniciarCamara = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
    } catch (e) { console.error('No se pudo iniciar la cámara:', e); }
  }, []);

  // Congela en pantalla la foto que quedó registrada (object URL local, Q0)
  const congelarFoto = useCallback((blob) => {
    if (!blob) return;
    try {
      if (fotoUrlRef.current) URL.revokeObjectURL(fotoUrlRef.current);
      fotoUrlRef.current = URL.createObjectURL(blob);
      setFotoCongelada(fotoUrlRef.current);
    } catch (e) { /* noop */ }
  }, []);

  // Desde la pantalla de rechazo: volver a los botones para intentar de nuevo
  const reintentarMarca = useCallback(() => {
    setMotivoRechazo('');
    setMensaje('');
    setEstadoPrueba(null);
    if (fotoUrlRef.current) { try { URL.revokeObjectURL(fotoUrlRef.current); } catch (e) { /* noop */ } fotoUrlRef.current = null; }
    setFotoCongelada(null);
    setFase('listo');
    if (!streamRef.current) iniciarCamara();
  }, [iniciarCamara]);

  // Botón atrás del celular cierra el modal (History API)
  useEffect(() => {
    window.history.pushState({ marcaMovil: true }, '');
    const onPop = () => onClose && onClose();
    window.addEventListener('popstate', onPop);
    return () => { window.removeEventListener('popstate', onPop); };
  }, [onClose]);

  const cerrar = useCallback(() => {
    detenerCamara();
    if (window.history.state && window.history.state.marcaMovil) { window.history.back(); }
    else if (onClose) onClose();
  }, [detenerCamara, onClose]);

  // Rastro del rechazo por ubicación (25/08/2026). Antes se bloqueaba sin guardar
  // NADA: no había forma de saber cuántas veces pasaba ni a cuántos metros tiraba el
  // GPS — ciegos justo en el caso que duele. Fire-and-forget: si falla el guardado,
  // al colaborador no se le molesta (el aviso en pantalla es lo que importa).
  const registrarRechazoUbicacion = useCallback((pos, distancia) => {
    try {
      const { fecha, hora } = fechaHoraGT();
      addDoc(collection(db, 'asistencia_rechazos_ubicacion'), {
        usuarioEmail: miEmail,
        usuarioNombre: miNombre,
        fecha, hora,
        geo: { lat: pos.lat, lng: pos.lng, accuracy: Math.round(pos.accuracy || 0), distanciaOficina: Math.round(distancia), radio },
        origen: 'MOVIL',
        dispositivoId: getDispositivoId(),
        creadoEnMs: Date.now(),
        timestamp: serverTimestamp()
      }).catch(() => { /* best-effort */ });
    } catch (e) { /* noop */ }
  }, [miEmail, miNombre, radio]);

  // Secuencia de preparación. Se corre al abrir y TAMBIÉN desde el botón "Reintentar"
  // de la pantalla "fuera del área" (antes había que cerrar y volver a abrir el modal).
  const preparar = useCallback(async () => {
    setFase('cargando');
    setMensaje('Un momento por favor…');

    // 0) ¿Tiene horario "sin hora fija" HOY? (su expediente: 1 lectura). Si no se puede leer, sigue el modo de siempre
    //    (con geocerca): ante la duda, la regla más estricta.
    let jornada = false;
    try {
      const expId = user && (user.id || user.uid);
      if (expId) {
        const ex = await getDoc(doc(db, 'expedientes', expId));
        const dl = (ex.exists() && ex.data().datosLaborales) || {};
        jornada = esSinHoraFija(horarioVigente(dl.horarios, dl.horario, fechaHoraGT().fecha));
      }
    } catch (e) { console.error('No se pudo leer el horario propio:', e); }
    if (!montadoRef.current) return;
    setModoJornada(jornada);

    let pos = null;
    if (jornada) {
      // 1') SIN GEOCERCA: la ubicación se busca unos segundos para dejarla de evidencia; si no se consigue (GPS
      //     apagado, sin permiso o sin señal) se marca igual y la marca va a Revisión (Toto, 23/10/2026).
      setMensaje('Buscando tu ubicación para guardarla con la marca…');
      try {
        pos = await obtenerUbicacion({
          maxMs: 15000,
          onLectura: (m) => { if (montadoRef.current) setMensaje(`Buscando tu ubicación… (margen ±${Math.round(m)} m)`); }
        });
      } catch (e) { pos = null; }
      if (!montadoRef.current) return;
      setGeo(pos ? { ...pos, distancia: (oficinaLat && oficinaLng) ? distanciaMetros(oficinaLat, oficinaLng, pos.lat, pos.lng) : null } : null);
    } else {
      // 0) Config válida
      if (!oficinaLat || !oficinaLng) { setFase('configError'); return; }

      // 1) UBICACIÓN PRIMERO — es el primer filtro: hasta no tenerla, no se muestra
      //    ni la cámara ni los botones (pedido de Toto).
      setMensaje('Detectando tu ubicación… esperá un momento por favor.');
      try {
        pos = await obtenerUbicacion({
          onLectura: (m) => { if (montadoRef.current) setMensaje(`Afinando tu ubicación… (margen ±${Math.round(m)} m)`); }
        });
      } catch (e) {
        if (!montadoRef.current) return;
        setFase('bloqueadoUbicacion');
        return;
      }
      if (!montadoRef.current) return;
      const distancia = distanciaMetros(oficinaLat, oficinaLng, pos.lat, pos.lng);
      setGeo({ ...pos, distancia });
      // Regla de Toto: si la distancia MEDIDA supera el radio configurado -> NO deja marcar.
      // (El margen de error del GPS no entra en esta cuenta; solo se guarda como dato.)
      if (distancia > radio) {
        registrarRechazoUbicacion(pos, distancia);
        setFase('fueraArea');
        return;
      }
    }

    // 2) Modelos + rostro registrado del propio colaborador
    setMensaje(jornada && !pos ? 'Preparando la cámara…' : 'Ubicación obtenida. Preparando la cámara…');
    let emp = null;
    try {
      await cargarModelos();
      const snap = await getDoc(doc(db, 'asistencia_rostros', emailKeyDe(miEmail)));
      if (snap.exists()) emp = { id: snap.id, ...snap.data() };
    } catch (e) { console.error('Error cargando rostro/modelos:', e); }
    if (!montadoRef.current) return;
    if (!emp || !emp.activo || !Array.isArray(emp.tomas) || emp.tomas.length === 0) { setFase('sinRostro'); return; }
    setEmpleado(emp);

    // 3) Marcas de hoy -> próxima marca válida
    if (jornada) {
      // Jornada: hoy + los 7 días anteriores en UNA consulta (índice usuarioEmail+fecha, el de Mi Asistencia), para
      // saber si su última jornada quedó sin fin y avisarle (Toto, 23/10/2026, "a+b").
      let marcasHoy = []; let sinFin = '';
      try {
        const { fecha } = fechaHoraGT();
        const ms = await getDocs(query(collection(db, 'asistencia_marcas'), where('usuarioEmail', '==', miEmail), where('fecha', '>=', fechaGTHace(7)), where('fecha', '<=', fecha)));
        const marcas = ms.docs.map((d) => d.data()).filter((m) => !m.anulada).sort((a, b) => minHora(a) - minHora(b));
        marcasHoy = marcas.filter((m) => m.fecha === fecha);
        const ultimoDia = marcas.map((m) => m.fecha).filter((f) => f < fecha).sort().pop();
        if (ultimoDia) {
          const delDia = marcas.filter((m) => m.fecha === ultimoDia);
          if (opcionesJornada(delDia).includes('SALIDA')) {
            const [, mm, dd] = ultimoDia.split('-');
            sinFin = `El ${dd}/${mm} no marcaste el fin de tu jornada. Avisale a RRHH para que lo complete.`;
          }
        }
      } catch (e) { console.error('Error leyendo marcas de la jornada:', e); }
      if (!montadoRef.current) return;
      setAvisoSinFin(sinFin);
      const ops = opcionesJornada(marcasHoy);
      if (ops.length === 0) {
        const fin = marcasHoy.slice().reverse().find((m) => m.tipo === 'SALIDA');
        setSalidaHora(fin ? (fin.hora || '') : '');
        setFase('cerrada');
        return;
      }
      setMensaje('');
      setOpciones(ops);
      setFase('listo');
      iniciarCamara();
      return;
    }
    let ultimo = null; let ultSalida = '';
    try {
      const { fecha } = fechaHoraGT();
      const ms = await getDocs(query(collection(db, 'asistencia_marcas'), where('usuarioEmail', '==', miEmail), where('fecha', '==', fecha)));
      const marcas = ms.docs.map((d) => d.data()).filter((m) => !m.anulada).sort((a, b) => minHora(a) - minHora(b));
      if (marcas.length) ultimo = marcas[marcas.length - 1].tipo;
      const us = marcas.slice().reverse().find((m) => m.tipo === 'SALIDA');
      ultSalida = us ? (us.hora || '') : '';
    } catch (e) { console.error('Error leyendo marcas de hoy:', e); }
    if (!montadoRef.current) return;
    if (ultimo === 'SALIDA') { setSalidaHora(ultSalida); setFase('cerrada'); return; }

    setMensaje(''); // limpiar el "detectando ubicación…" (no debe quedar pegado en la pantalla)
    setOpciones(opcionesSiguiente(ultimo));
    setFase('listo');
    iniciarCamara();
  }, [oficinaLat, oficinaLng, radio, miEmail, user, iniciarCamara, registrarRechazoUbicacion]);

  useEffect(() => {
    montadoRef.current = true;
    preparar();
    return () => { montadoRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    detenerCamara();
    if (fotoUrlRef.current) { try { URL.revokeObjectURL(fotoUrlRef.current); } catch (e) { /* noop */ } }
  }, [detenerCamara]);

  // Al volver a la pantalla de marcar (ej. desde el rechazo), re-conectar el stream
  // al <video>: el elemento se desmonta en las pantallas intermedias y pierde el
  // srcObject aunque la cámara siga encendida.
  useEffect(() => {
    if (fase === 'listo' && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [fase]);

  const marcar = async (tipo) => {
    // En modo jornada se puede marcar sin ubicación (va a Revisión); en el de siempre, no.
    if (procesandoRef.current || !empleado || (!geo && !modoJornada)) return;
    procesandoRef.current = true;
    setMensaje('');

    // Cámara muerta (no arrancó o el video no tiene cuadros): reintentar una vez y,
    // si sigue muerta, avisar A PANTALLA COMPLETA que la marca NO se registró.
    if (!videoRef.current || !videoRef.current.videoWidth) {
      await iniciarCamara();
      await new Promise((r) => setTimeout(r, 700));
      if (!videoRef.current || !videoRef.current.videoWidth) {
        procesandoRef.current = false;
        setMotivoRechazo('camara');
        setFase('rechazo');
        return;
      }
    }
    setFase('procesando');

    // Prueba de vida GUIADA: pide la sonrisa y, si la persona ya venía sonriendo,
    // le pide ponerse seria un momento y volver a sonreír. Al detectarla muestra un
    // cheque verde sobre el video. Si aun así no se detecta, falla EN SILENCIO:
    // la marca entra normal y queda con bandera para que RRHH la vea en Revisión.
    let liveness = { ok: false };
    setTipoProceso(tipo);
    setEstadoPrueba('SONRIA');
    try {
      if (videoRef.current) {
        liveness = await detectarPruebaVida(videoRef.current, { timeoutMs: 10000, onEstado: (e) => setEstadoPrueba(e) });
      }
    } catch (e) { console.error('Error en prueba de vida:', e); }

    // Cámara CONGELADA (imagen clavada en un cuadro fijo, detectado en faceModels):
    // con el video muerto ningún gesto puede registrarse jamás. Se corta con la
    // pantalla de rechazo y se APAGA la cámara para que "Intentar de nuevo" levante
    // un stream fresco. Al 2º intento seguido, la marca entra igual "para revisión"
    // (a una persona real no se la bloquea por una cámara trabada).
    if (!liveness.ok && liveness.via === 'CONGELADA') {
      intentosCongeladaRef.current += 1;
      if (intentosCongeladaRef.current < 2) {
        detenerCamara();
        procesandoRef.current = false;
        setEstadoPrueba(null);
        setMotivoRechazo('congelada');
        setFase('rechazo');
        return;
      }
    }

    // Foto AL INSTANTE de pasar la prueba (todavía sonriendo), pero el cheque verde
    // TODAVÍA no: el reconocimiento sigue trabajando y la persona debe quedarse
    // frente a la cámara (si el cheque saliera acá, bajaría el teléfono antes de
    // tiempo y el rostro no llegaría a verificarse — decisión de Toto).
    let blob = null;
    if (liveness.ok) {
      try { if (videoRef.current) blob = await fotoWebpDesde(videoRef.current, videoRef.current.videoWidth, videoRef.current.videoHeight); } catch (e) { /* noop */ }
    }
    setEstadoPrueba('VERIFICANDO');

    const { fecha, hora } = fechaHoraGT();

    let match = { estado: 'SIN_MATCH', distancia: null };
    let rostroVisto = false; // ¿face-api llegó a detectar ALGÚN rostro?
    let lecturasDanadas = 0; // cuadros con parecido imposible (>1.2) — defecto del aparato, no de la persona
    try {
      if (videoRef.current) {
        // Hasta 5 intentos (antes 3 — ajuste 25/08/2026): justo después del gesto hay
        // movimiento/blur y un solo cuadro a veces no detecta el rostro (causaba "Sin
        // coincidencia" falsos); a los casos que quedaban a un pelo del umbral, un par
        // de cuadros extra los rescata. Se conserva la MEJOR lectura (menor distancia),
        // no la última: antes un último cuadro malo pisaba a uno bueno del medio.
        // Cada intento con TOPE de 4s y la fase completa con TOPE de 12s: en teléfonos
        // con el WebGL dañado la detección puede COLGARSE y el colaborador quedaba
        // pegado en "Verificando tu rostro…" sin marca (caso real 25/08/2026).
        const inicioVerif = Date.now();
        for (let intento = 0; intento < 5 && Date.now() - inicioVerif < 12000; intento++) {
          const det = await detectarRostroConTope(videoRef.current, 4000);
          if (det && det.descriptor) {
            rostroVisto = true;
            const m = evaluarMatch(det.descriptor, empleado.tomas);
            // Lectura DAÑADA: un parecido >1.2 es imposible entre dos caras reales
            // (entre personas distintas da ~0.6-1.0; se midió 1.84 en un teléfono con
            // el defecto). Es basura del aparato, NO evidencia de suplantación: se
            // descarta para que no pise una lectura buena ni acuse a la persona.
            if (m.distancia != null && m.distancia > 1.2) { lecturasDanadas++; }
            else {
              if (m.distancia != null && (match.distancia == null || m.distancia < match.distancia)) match = m;
              if (match.estado === 'ALTA') break; // confirmado; si no, reintenta por un cuadro mejor
            }
          }
          await new Promise((r) => setTimeout(r, 180));
        }
        if (!blob) blob = await fotoWebpDesde(videoRef.current, videoRef.current.videoWidth, videoRef.current.videoHeight);
      }
    } catch (e) { console.error('Error en verificación facial:', e); }

    // Regla AJUSTADA (decisión de Toto, jul 2026): si no se detectó NINGÚN rostro
    // Y tampoco pasó la prueba de sonrisa → NO se marca (aviso a pantalla completa)…
    // pero solo el PRIMER intento. Si pasó la sonrisa (evidencia de persona en vivo)
    // o ya es el 2º intento seguido rechazado, la marca entra igual "para revisión"
    // con nota — a una persona real no se le puede negar marcar por mala luz/ángulo.
    if (!rostroVisto && !liveness.ok) {
      intentosSinRostroRef.current += 1;
      if (intentosSinRostroRef.current < 2) {
        procesandoRef.current = false;
        setEstadoPrueba(null);
        setMotivoRechazo('rostro');
        setFase('rechazo');
        return;
      }
    }

    // Reconocimiento TERMINADO: ya no se necesita a la persona frente a la cámara.
    // Se apaga el video en vivo y se congela la foto que quedó registrada; el
    // cheque verde recién sale cuando la marca YA quedó guardada en el servidor
    // (el celular no tiene cola offline: mostrarlo antes sería mentir).
    detenerCamara();
    congelarFoto(blob);
    setEstadoPrueba('GUARDANDO');

    // ¿Requiere revisión? (cara no confirmada o GPS de mala calidad)
    const margen = geo ? (geo.accuracy || 0) : 0;
    // Si la distancia medida supera el radio, NI llega acá (se bloquea antes).
    // Lo único que se marca para revisión es un GPS de pésima calidad (margen de
    // error > 100m): no es una distancia, es "el teléfono casi no sabe dónde está",
    // y conviene que RRHH le eche un ojo a esa marca.
    const geoLimite = !!geo && margen > 100;
    // Modo jornada sin ubicación: la marca entra y va a Revisión (Toto, 23/10/2026).
    const sinUbicacion = modoJornada && !geo;
    const faceOk = match.estado === 'ALTA';
    let requiereRevision = false;
    const motivos = [];
    if (!rostroVisto) { requiereRevision = true; motivos.push(liveness.ok ? 'No se detectó el rostro con claridad (sí pasó la prueba de sonrisa)' : 'No se detectó el rostro (se registró al 2º intento)'); }
    else if (!faceOk) {
      requiereRevision = true;
      // Si no hubo NINGUNA lectura válida y sí parecidos imposibles, el problema es
      // el aparato, no la persona — que RRHH lo sepa al revisar la foto.
      motivos.push(lecturasDanadas >= 2 && match.distancia == null
        ? 'Lectura facial dañada en este dispositivo (valores imposibles; probable falla del teléfono — revisar la foto)'
        : 'No se confirmó el rostro');
    }
    if (geoLimite) { requiereRevision = true; motivos.push('GPS muy impreciso (margen de error mayor a 100 m)'); }
    if (sinUbicacion) { requiereRevision = true; motivos.push('Sin ubicación (GPS apagado, sin permiso o sin señal)'); }
    if (!liveness.ok) { requiereRevision = true; motivos.push(liveness.via === 'CONGELADA' ? 'La cámara se congeló durante la prueba de vida (se registró al 2º intento)' : 'No se confirmó persona en vivo (sin sonrisa)'); }
    // Al empleado solo se le avisa por rostro o ubicación; la sonrisa falla en silencio.
    const avisarRevision = !faceOk || geoLimite || sinUbicacion;

    // Ubicación de la marca. En modo jornada es solo evidencia (sin "dentro del radio": no hay geocerca) y la
    // distancia a la oficina queda como dato (null si la oficina no está configurada).
    const geoMarca = modoJornada
      ? (geo ? { lat: geo.lat, lng: geo.lng, accuracy: Math.round(margen), distanciaOficina: geo.distancia != null ? Math.round(geo.distancia) : null } : null)
      : { lat: geo.lat, lng: geo.lng, accuracy: Math.round(margen), distanciaOficina: Math.round(geo.distancia), dentroDelRadio: geo.distancia <= radio };

    const marca = {
      usuarioEmail: miEmail,
      usuarioNombre: miNombre,
      tipo, fecha, hora,
      // `lecturasDanadas` queda de diagnóstico: cuántos cuadros dieron parecido imposible
      match: { ...match, ...(lecturasDanadas ? { lecturasDanadas } : {}) },
      liveness: { ok: liveness.ok, gesto: 'SONRISA', via: liveness.via ?? null, smileMin: liveness.smileMin ?? null, smileMax: liveness.smileMax ?? null, frames: liveness.frames ?? 0 },
      geo: geoMarca,
      ...(modoJornada ? { jornada: true } : {}),
      origen: 'MOVIL',
      dispositivoId: getDispositivoId(),
      requiereRevision,
      revisionMotivo: motivos.join('; '),
      creadoEnMs: Date.now()
    };

    try {
      let fotoUrl = ''; let fotoPath = '';
      if (blob) {
        fotoPath = `asistencia_fotos/${fecha}/${emailKeyDe(miEmail)}_${(hora || '').replace(':', '')}_${Date.now()}_${parteAlAzar()}.webp`;
        const sref = ref(storage, fotoPath);
        await uploadBytes(sref, blob, { contentType: (blob && blob.type) || 'image/webp' });
        fotoUrl = await getDownloadURL(sref);
      }
      await addDoc(collection(db, 'asistencia_marcas'), { ...marca, fotoUrl, fotoPath, timestamp: serverTimestamp() });
    } catch (e) {
      console.error('Error registrando la marca móvil:', e);
      procesandoRef.current = false;
      setEstadoPrueba(null);
      setMotivoRechazo('red');
      setFase('rechazo');
      return;
    }

    intentosSinRostroRef.current = 0; // marcó: se limpian los contadores de rechazos
    intentosCongeladaRef.current = 0;

    // Marca YA guardada en el servidor: ahora sí el cheque verde (mínimo ~0.7s)
    setEstadoPrueba('CHECK');
    await new Promise((r) => setTimeout(r, 700));
    setEstadoPrueba(null);
    setResultado({ tipo, hora, match, avisarRevision, sinUbicacion });
    setFase('resultado');
    procesandoRef.current = false;
  };

  // ===== Estilos =====
  const fondo = { position: 'fixed', inset: 0, zIndex: 4000, background: '#2d2658', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', boxSizing: 'border-box', textAlign: 'center' };
  const btnCerrar = (
    <button onClick={cerrar} aria-label="Cerrar" style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '10px', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
      <XMarkIcon className="w-6 h-6" />
    </button>
  );
  const btnSecundario = { marginTop: '24px', padding: '12px 28px', borderRadius: '10px', border: 'none', background: 'rgba(255,255,255,0.18)', color: 'white', fontSize: '16px', cursor: 'pointer' };

  // ===== Render =====
  if (fase === 'cargando') {
    return (
      <div style={fondo}>
        {btnCerrar}
        <span style={{ display: 'inline-flex', marginBottom: '16px' }} className="pulse-soft"><ClockIcon className="w-16 h-16" /></span>
        <h1 style={{ color: 'white', margin: 0, fontSize: '22px' }}>Un momento...</h1>
        <p style={{ color: 'white', opacity: 0.85, marginTop: '8px' }}>{mensaje || 'Preparando la cámara y verificando tu ubicación.'}</p>
      </div>
    );
  }

  if (fase === 'configError') {
    return (
      <div style={fondo}>
        {btnCerrar}
        <span style={{ display: 'inline-flex', marginBottom: '16px', color: '#ffd43b' }}><ExclamationTriangleIcon className="w-16 h-16" /></span>
        <h1 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '22px' }}>Falta configurar la oficina</h1>
        <p style={{ color: 'white', opacity: 0.85, maxWidth: '420px' }}>Todavía no se fijó la ubicación de la oficina. Pedile a RRHH que la configure en el módulo de Asistencia.</p>
        <button onClick={cerrar} style={btnSecundario}>Entendido</button>
      </div>
    );
  }

  if (fase === 'sinRostro') {
    return (
      <div style={fondo}>
        {btnCerrar}
        <span style={{ display: 'inline-flex', marginBottom: '16px', color: '#ffd43b' }}><ExclamationTriangleIcon className="w-16 h-16" /></span>
        <h1 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '22px' }}>Aún no tenés rostro registrado</h1>
        <p style={{ color: 'white', opacity: 0.85, maxWidth: '420px' }}>Para marcar desde el celular, RRHH primero debe registrar tu rostro y tu PIN en tu expediente.</p>
        <button onClick={cerrar} style={btnSecundario}>Entendido</button>
      </div>
    );
  }

  if (fase === 'bloqueadoUbicacion') {
    return (
      <div style={fondo}>
        {btnCerrar}
        <span style={{ display: 'inline-flex', marginBottom: '16px', color: '#ffd43b' }}><MapPinIcon className="w-16 h-16" /></span>
        <h1 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '22px' }}>Necesitamos tu ubicación</h1>
        <p style={{ color: 'white', opacity: 0.85, maxWidth: '440px' }}>
          Para marcar desde el celular tenés que permitir el acceso a la ubicación (GPS). Activá el GPS, dale "Permitir" cuando el navegador lo pida, y volvé a intentar. Si no podés, marcá en el kiosko.
        </p>
        <button onClick={cerrar} style={btnSecundario}>Cerrar</button>
      </div>
    );
  }

  if (fase === 'fueraArea') {
    return (
      <div style={fondo}>
        {btnCerrar}
        <span style={{ display: 'inline-flex', marginBottom: '16px', color: '#ff8787' }}><MapPinIcon className="w-16 h-16" /></span>
        <h1 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '22px' }}>No parece que estés en la oficina</h1>
        <p style={{ color: 'white', opacity: 0.85, maxWidth: '440px' }}>
          Solo se puede marcar desde el celular estando en las instalaciones. Acercate a la oficina e intentá de nuevo, o marcá en el kiosko.
        </p>
        {geo && (
          <p style={{ color: 'white', opacity: 0.55, fontSize: '13px', marginTop: '6px' }}>
            Estás a unos {Math.round(geo.distancia)} m del punto de la oficina (margen del GPS: ±{Math.round(geo.accuracy || 0)} m).
          </p>
        )}
        {/* Reintentar re-corre la detección de ubicación: el GPS afina con los segundos
            y el segundo intento suele dar una lectura mucho mejor que el primero. */}
        <button className="btn-press" onClick={preparar} style={{ marginTop: '24px', padding: '14px 34px', borderRadius: '10px', border: 'none', background: '#667eea', color: 'white', fontSize: '17px', fontWeight: 700, cursor: 'pointer' }}>
          Reintentar
        </button>
        <button onClick={cerrar} style={{ ...btnSecundario, marginTop: '12px' }}>Entendido</button>
      </div>
    );
  }

  if (fase === 'cerrada') {
    return (
      <div style={fondo}>
        {btnCerrar}
        <span style={{ display: 'inline-flex', marginBottom: '16px', color: '#ffd43b' }}><ExclamationTriangleIcon className="w-16 h-16" /></span>
        <h1 style={{ color: 'white', margin: '0 0 10px 0', fontSize: '22px' }}>Hola, {miNombre}</h1>
        <p style={{ color: 'white', fontSize: '17px', margin: '0 0 6px 0' }}>Ya marcaste {modoJornada ? <>el <strong>fin de tu jornada</strong></> : <>tu <strong>salida</strong></>} de hoy{salidaHora ? ` a las ${hora12(salidaHora)}` : ''}.</p>
        <p style={{ color: 'white', opacity: 0.85, maxWidth: '440px', margin: 0 }}>Si fue un error, acercate a <strong>Recursos Humanos</strong> para que lo corrijan.</p>
        <button onClick={cerrar} style={btnSecundario}>Listo</button>
      </div>
    );
  }

  if (fase === 'rechazo') {
    const textoRechazo = motivoRechazo === 'camara'
      ? 'La cámara de tu teléfono no está funcionando. Cerrá y volvé a abrir, y si sigue igual marcá en el kiosko o avisale a RRHH.'
      : motivoRechazo === 'congelada'
        ? 'La imagen de la cámara se congeló. Tocá "Intentar de nuevo" para reiniciarla — si vuelve a pasar, tu marca entrará igual y quedará para revisión de RRHH.'
      : motivoRechazo === 'red'
        ? 'No se pudo guardar por un problema de conexión. Revisá tu internet e intentá de nuevo — hasta no ver el cheque verde, la marca NO quedó.'
        : 'No se detectó tu rostro. Ponete de frente a la cámara, con buena luz, e intentá de nuevo — si tampoco se detecta, la marca entrará igual y quedará para revisión de RRHH.';
    return (
      <div style={{ ...fondo, background: '#7a1f2e' }}>
        {btnCerrar}
        <span style={{ display: 'inline-flex', marginBottom: '16px', color: '#ffd43b' }}><ExclamationTriangleIcon className="w-20 h-20" /></span>
        <h1 style={{ color: 'white', margin: '0 0 10px 0', fontSize: '26px' }}>Tu marca NO se registró</h1>
        <p style={{ color: 'white', fontSize: '16px', maxWidth: '440px', margin: 0, lineHeight: 1.45 }}>{textoRechazo}</p>
        <button className="btn-press" onClick={reintentarMarca} style={{ marginTop: '26px', padding: '14px 34px', borderRadius: '10px', border: '2px solid rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.18)', color: '#ffffff', fontSize: '17px', fontWeight: 700, cursor: 'pointer' }}>
          Intentar de nuevo
        </button>
      </div>
    );
  }

  if (fase === 'listo' || fase === 'procesando') {
    const procesando = fase === 'procesando';
    // El botón principal (la salida del día, si compite con el almuerzo, va aparte como emergencia)
    const emergencia = opciones.length > 1 && opciones.includes('SALIDA') ? 'SALIDA' : null;
    const principales = emergencia ? opciones.filter((t) => t !== 'SALIDA') : opciones;
    const principal = principales[0];
    // Guía por estado del proceso (banda sobre el video)
    const GUIA = {
      SONRIA: { Icono: FaceSmileIcon, color: '#ffd43b', texto: `¡Sonreíle a la cámara!${tipoProceso === 'ENTRADA' ? (modoJornada ? ' ¡Buena jornada!' : ' ¡Bienvenid@!') : ''}` },
      PRIMERO_SERIO: { Icono: FaceFrownIcon, color: '#ffd43b', texto: 'Dejá de sonreír un momento…' },
      VERIFICANDO: { Icono: ClockIcon, color: 'white', texto: 'Verificando tu rostro… quedate frente a la cámara' },
      GUARDANDO: { Icono: ClockIcon, color: 'white', texto: 'Guardando tu marca…' }
    };
    const guia = procesando ? GUIA[estadoPrueba] : null;
    return (
      <div style={fondo}>
        {!procesando && btnCerrar}
        <h2 style={{ color: 'white', margin: '0 0 2px 0', fontSize: '22px' }}>Hola, {miNombre}</h2>
        <p style={{ color: 'white', opacity: 0.85, marginTop: 0, marginBottom: '14px' }}>
          {procesando
            ? `Registrando tu ${(tipoTexto(tipoProceso) || 'marca').toLowerCase()}…`
            : `Mirá a la cámara y presioná el botón "${etiquetaBoton(principal) || ''}"`}
        </p>
        {avisoSinFin && !procesando && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', maxWidth: '440px', margin: '0 0 12px 0', padding: '10px 12px', borderRadius: '10px', background: 'rgba(255,212,59,0.15)', border: '1px solid rgba(255,212,59,0.6)', color: '#ffe8a3', fontSize: '14px', textAlign: 'left' }}>
            <span style={{ display: 'inline-flex', flexShrink: 0, color: '#ffd43b' }}><ExclamationTriangleIcon className="w-5 h-5" /></span>
            <span>{avisoSinFin}</span>
          </div>
        )}

        {/* La guía va SOBRE el video (banda semitransparente): cero saltos de layout.
            Cuadro grande y más ALTO que ancho (formato retrato, como el teléfono);
            responsivo: el ancho sigue al 92% de la pantalla y la altura lo acompaña. */}
        <div style={{ position: 'relative', width: 'min(440px, 92vw)', aspectRatio: '4 / 5', borderRadius: '14px', overflow: 'hidden', background: '#000', marginBottom: '12px', flexShrink: 0 }}>
          <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
          {fotoCongelada && (
            <img src={fotoCongelada} alt="Foto registrada" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
          )}
          {guia && (
            <div className="fade-in" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', padding: '10px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <span style={{ color: guia.color, display: 'inline-flex', flexShrink: 0 }}><guia.Icono className="w-7 h-7" /></span>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'white' }}>{guia.texto}</div>
            </div>
          )}
          {procesando && estadoPrueba === 'CHECK' && (
            <div className="check-pop" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
              <span style={{ color: '#37d67a', display: 'inline-flex' }}><CheckCircleIcon className="w-20 h-20" /></span>
            </div>
          )}
        </div>

        {geo && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', marginBottom: '16px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#69db7c', fontWeight: 600 }}>
              <CheckCircleIcon className="w-4 h-4" /> Ubicación obtenida
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', opacity: 0.75 }}>
              <MapPinIcon className="w-4 h-4" /> {modoJornada ? 'Se guarda con tu marca' : 'En la oficina'} (±{Math.round(geo.accuracy || 0)} m)
            </div>
          </div>
        )}
        {!geo && modoJornada && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#ffd43b', marginBottom: '16px', maxWidth: '360px' }}>
            <MapPinIcon className="w-4 h-4" /> Sin ubicación: tu marca entra igual, para revisión de RRHH.
          </div>
        )}

        {mensaje && <div style={{ color: '#ff8787', marginBottom: '14px', fontSize: '14px', maxWidth: '320px' }}>{mensaje}</div>}

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '520px' }}>
          {principales.map((tipo) => (
            <button
              key={tipo}
              className="btn-press hover-bright"
              disabled={procesando}
              onClick={() => marcar(tipo)}
              style={{ minWidth: '180px', padding: '16px 22px', borderRadius: '12px', border: 'none', cursor: procesando ? 'wait' : 'pointer', fontSize: '17px', fontWeight: 600, color: 'white', background: '#667eea', opacity: procesando ? 0.6 : 1 }}
            >
              {etiquetaBoton(tipo) || tipoTexto(tipo)}
            </button>
          ))}
        </div>
        {emergencia && !procesando && (
          <div style={{ position: 'absolute', top: '16px', left: '16px', width: '150px', textAlign: 'center' }}>
            <button
              className="btn-press"
              onClick={() => marcar(emergencia)}
              style={{ width: '100%', padding: '9px 10px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.12)', color: 'white', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            >
              Marcar Salida
            </button>
            <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '5px', lineHeight: 1.35 }}>
              Solo por emergencia (salida antes de tiempo). Avisale a RRHH.
            </div>
          </div>
        )}
      </div>
    );
  }

  // fase === 'resultado'
  const r = resultado || {};
  return (
    <div style={fondo}>
      {btnCerrar}
      <span style={{ display: 'inline-flex', marginBottom: '12px', color: r.avisarRevision ? '#ffc107' : '#37d67a' }} className="check-pop">
        {r.avisarRevision ? <ExclamationTriangleIcon className="w-16 h-16" /> : <CheckCircleIcon className="w-16 h-16" />}
      </span>
      {fotoCongelada && (
        <img src={fotoCongelada} alt="Tu foto registrada" style={{ width: '140px', borderRadius: '12px', transform: 'scaleX(-1)', marginBottom: '12px', border: '2px solid rgba(255,255,255,0.35)' }} />
      )}
      <h1 style={{ color: 'white', margin: '0 0 6px 0', fontSize: '24px' }}>{modoJornada ? `${tipoTexto(r.tipo) || 'Marca'} registrado` : `${TIPOS[r.tipo] || 'Marca'} registrada`}</h1>
      <div style={{ fontSize: '40px', fontWeight: 700, marginBottom: '6px' }}>{hora12(r.hora)}</div>
      <p style={{ color: 'white', opacity: 0.85, margin: 0 }}>{miNombre}</p>
      {r.avisarRevision && (
        <p style={{ color: '#ffd43b', marginTop: '12px', maxWidth: '420px' }}>
          Quedó registrada para revisión de RRHH. No te preocupes, tu marca está guardada.
          {r.sinUbicacion ? ' La próxima vez activá el GPS y permití la ubicación.' : ''}
        </p>
      )}
      <button onClick={cerrar} style={btnSecundario}>Listo</button>
    </div>
  );
}

export default MarcarAsistenciaMovil;
